import React, { useState, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { AgGridReact } from "ag-grid-react";
import { 
    Calculator, 
    CircleInfo, 
    FileCircleXmark, 
    ScaleBalanced,
    FileLines,
    RefreshCw,
    // InfoCircle
    UserTag
} from '../../../../../../../components/icons/Icons';
import { getReviwQueueDeshbordPath } from "../../../../../../../services/config";
import { commonSaveData } from "../../../../../../../redux/slices/commonSlice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const SlabWiseTds = ({ staffId, regimeType = 'new', refreshTrigger = null }) => {
    const [gridApi, setGridApi] = useState(null);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [apiData, setApiData] = useState(null);
    const dispatch = useDispatch();

    // Fetch data directly when component mounts
    useEffect(() => {
        const fetchSlabData = async () => {
            if (!staffId) {
                console.log("No staffId provided, skipping fetch");
                return;
            }

            setLoading(true);
            setApiError(null);

            try {
                const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
                const userId = userData.user_id || userData.id || "123";

                const payload = {
                    path: getReviwQueueDeshbordPath,
                    data: {
                        userId: userId,
                        staffId: Number(staffId),
                        employeeId: "FY2025-26",
                        section: "all",
                        financialYear: "2025-26",
                    },
                };

                console.log("SlabWiseTds: Fetching data for staffId:", staffId);

                const response = await dispatch(commonSaveData(payload)).unwrap();

                console.log("SlabWiseTds: API Response:", response);

                if (response && response.code === 200 && response.status === true) {
                    if (response.data) {
                        setApiData(response.data);
                    } else {
                        throw new Error("No data field in response");
                    }
                } else {
                    throw new Error(response?.message || "Invalid API response");
                }

            } catch (error) {
                console.error("SlabWiseTds: Load Error:", error);
                setApiError(error);
                toast.error("Failed to load TDS slab data");
            } finally {
                setLoading(false);
            }
        };

        fetchSlabData();
    }, [staffId, dispatch, refreshTrigger]);

    const onGridReady = (params) => {
        setGridApi(params.api);
        if (params.api) {
            params.api.sizeColumnsToFit();
        }
    };

    // =========================================================================
    // 1. LOADING & ERROR STATES
    // =========================================================================
    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-purple" style={{ width: '3rem', height: '3rem' }} />
                <p className="color-gray mt-3">Loading TDS slab calculation...</p>
            </div>
        );
    }

    if (apiError) {
        return (
            <div className="text-center py-5">
                <FileCircleXmark className="text-red" style={{ fontSize: '64px' }} />
                <h4 className="color-white mt-3">Error Loading TDS Data</h4>
                <p className="color-gray">{apiError?.message || "Failed to fetch tax calculation"}</p>
                <button 
                    className="btn btn-purple mt-3"
                    onClick={() => {
                        setApiError(null);
                        setLoading(true);
                        setTimeout(() => {
                            const fetchData = async () => {
                                try {
                                    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
                                    const userId = userData.user_id || userData.id || "123";
                                    
                                    const payload = {
                                        path: getReviwQueueDeshbordPath,
                                        data: {
                                            userId: userId,
                                            staffId: Number(staffId),
                                            employeeId: "FY2025-26",
                                            section: "all",
                                            financialYear: "2025-26",
                                        },
                                    };
                                    
                                    const response = await dispatch(commonSaveData(payload)).unwrap();
                                    if (response.code === 200 && response.data) {
                                        setApiData(response.data);
                                    }
                                } catch (err) {
                                    setApiError(err);
                                } finally {
                                    setLoading(false);
                                }
                            };
                            fetchData();
                        }, 500);
                    }}
                >
                    <RefreshCw className="mr-2" />
                    Retry
                </button>
            </div>
        );
    }

    // Check if staffId exists
    if (!staffId) {
        return (
            <div className="text-center py-5">
                <CircleInfo className="text-yellow" style={{ fontSize: '64px' }} />
                <h4 className="color-white mt-3">No Employee Selected</h4>
                <p className="color-gray">Please select an employee to view TDS slab details.</p>
            </div>
        );
    }

    // Check if we have API data
    if (!apiData) {
        return (
            <div className="text-center py-5">
                <FileLines className="text-yellow" style={{ fontSize: '64px' }} />
                <h4 className="color-white mt-3">No TDS Configuration Found</h4>
                <p className="color-gray">Payroll configuration is not submitted or no TDS data available for this employee.</p>
                <div className="mt-4">
                    <p className="small color-gray mb-2">Employee Details:</p>
                    <div className="d-flex justify-content-center gap-3">
                        <span className="badge badge-info">
                            Staff ID: {staffId}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    // Extract data from API response
    const employeeInfo = apiData.employeeInfo || {};
    const sections = apiData.sections || {};
    const overallStatus = apiData.overallStatus || {};
    const taxData = apiData.taxData || {};
    
    // Check if we have valid employee data
    if (!employeeInfo.employeeName && !employeeInfo.employeeId) {
        return (
            <div className="text-center py-5">
                <FileLines className="text-yellow" style={{ fontSize: '64px' }} />
                <h4 className="color-white mt-3">Incomplete Employee Data</h4>
                <p className="color-gray">Employee information is missing from the API response.</p>
                <div className="mt-3">
                    <button 
                        className="btn btn-outline-purple"
                        onClick={() => {
                            setLoading(true);
                            const fetchData = async () => {
                                try {
                                    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
                                    const userId = userData.user_id || userData.id || "123";
                                    
                                    const payload = {
                                        path: getReviwQueueDeshbordPath,
                                        data: {
                                            userId: userId,
                                            staffId: Number(staffId),
                                            employeeId: "FY2025-26",
                                            section: "all",
                                            financialYear: "2025-26",
                                        },
                                    };
                                    
                                    const response = await dispatch(commonSaveData(payload)).unwrap();
                                    if (response.code === 200 && response.data) {
                                        setApiData(response.data);
                                        toast.success("Data refreshed");
                                    }
                                } catch (err) {
                                    toast.error("Failed to refresh data");
                                } finally {
                                    setLoading(false);
                                }
                            };
                            fetchData();
                        }}
                    >
                        <RefreshCw className="mr-1" /> Refresh Data
                    </button>
                </div>
            </div>
        );
    }

    // =========================================================================
    // 2. DYNAMIC DATA EXTRACTION FROM API RESPONSE
    // =========================================================================
    const employeeName = employeeInfo.employeeName || 'Employee';
    const employeeId = employeeInfo.employeeId || staffId;
    const panNumber = employeeInfo.employeePAN || 'N/A';
    const financialYear = employeeInfo.financialYear || '2025-26';

    // Extract income summary data from API
    const grossSalary = employeeInfo.grossSalary || 
                       sections.salary?.totalAnnualCTC || 
                       0;
    
    // Extract standard deduction - check multiple possible locations
    const standardDeduction = sections.deductions?.standardDeduction?.value || 
                             sections.standardDeduction?.value || 
                             employeeInfo.standardDeduction || 
                             50000; // Default if not found

    // Extract total deductions
    const totalDeductionsValue = sections.deductions?.totalDeduction || 
                                sections.totalDeductions || 
                                taxData.totalDeductions || 
                                0;

    // Extract HRA exemption
    const hraExemption = sections.hra?.annualHRAExemption?.value || 
                        sections.hra?.fields?.annualHRAExemption?.value || 
                        0;

    // Calculate taxable income from API data
    const taxableIncomeFromAPI = taxData.taxableIncome || 
                                employeeInfo.taxableIncome || 
                                Math.max(0, grossSalary - standardDeduction - hraExemption - totalDeductionsValue);

    // Extract tax calculation data
    const taxBeforeRebate = taxData.taxBeforeRebate || taxData.totalTaxBeforeRebate || 0;
    const rebate87A = taxData.rebate87A || taxData.rebateUnder87A || 0;
    const taxAfterRebate = taxData.taxAfterRebate || taxData.totalTaxAfterRebate || 0;
    const healthEducationCess = taxData.cess || taxData.healthEducationCess || 0;
    const annualTaxLiability = taxData.annualTaxLiability || taxData.totalTaxLiability || 0;
    const monthlyTds = taxData.monthlyTDS || taxData.monthlyTax || 
                      (annualTaxLiability > 0 ? Math.round(annualTaxLiability / 12) : 0);

    // Extract system vs HR override data
    const systemTds = taxData.systemTDS || taxData.calculatedTDS || monthlyTds;
    const hrOverrideTds = taxData.hrOverrideTDS || taxData.overriddenTDS || systemTds;

    // Extract tax slabs from API
    const taxSlabsFromAPI = taxData.taxSlabs || taxData.slabs || employeeInfo.taxSlabs || [];
    
    // Generate tax slabs if not available in API
    const generateTaxSlabs = () => {
        const taxableIncome = taxableIncomeFromAPI;
        const slabs = [];
        
        if (taxableIncome <= 0) return slabs;
        
        // New Regime Slabs (FY 2023-24 onwards)
        if (regimeType === 'new') {
            if (taxableIncome > 0) {
                const slab1 = Math.min(taxableIncome, 300000);
                slabs.push({
                    range: "₹0 - ₹3,00,000",
                    incomeInSlab: slab1,
                    rate: "0%",
                    taxInSlab: 0
                });
            }
            
            if (taxableIncome > 300000) {
                const slab2 = Math.min(taxableIncome - 300000, 300000);
                slabs.push({
                    range: "₹3,00,001 - ₹6,00,000",
                    incomeInSlab: slab2,
                    rate: "5%",
                    taxInSlab: slab2 * 0.05
                });
            }
            
            if (taxableIncome > 600000) {
                const slab3 = Math.min(taxableIncome - 600000, 300000);
                slabs.push({
                    range: "₹6,00,001 - ₹9,00,000",
                    incomeInSlab: slab3,
                    rate: "10%",
                    taxInSlab: slab3 * 0.10
                });
            }
            
            if (taxableIncome > 900000) {
                const slab4 = Math.min(taxableIncome - 900000, 300000);
                slabs.push({
                    range: "₹9,00,001 - ₹12,00,000",
                    incomeInSlab: slab4,
                    rate: "15%",
                    taxInSlab: slab4 * 0.15
                });
            }
            
            if (taxableIncome > 1200000) {
                const slab5 = Math.min(taxableIncome - 1200000, 500000);
                slabs.push({
                    range: "₹12,00,001 - ₹15,00,000",
                    incomeInSlab: slab5,
                    rate: "20%",
                    taxInSlab: slab5 * 0.20
                });
            }
            
            if (taxableIncome > 1500000) {
                const slab6 = taxableIncome - 1500000;
                slabs.push({
                    range: "Above ₹15,00,000",
                    incomeInSlab: slab6,
                    rate: "30%",
                    taxInSlab: slab6 * 0.30
                });
            }
        } else {
            // Old Regime Slabs
            if (taxableIncome > 0) {
                const slab1 = Math.min(taxableIncome, 250000);
                slabs.push({
                    range: "₹0 - ₹2,50,000",
                    incomeInSlab: slab1,
                    rate: "0%",
                    taxInSlab: 0
                });
            }
            
            if (taxableIncome > 250000) {
                const slab2 = Math.min(taxableIncome - 250000, 250000);
                slabs.push({
                    range: "₹2,50,001 - ₹5,00,000",
                    incomeInSlab: slab2,
                    rate: "5%",
                    taxInSlab: slab2 * 0.05
                });
            }
            
            if (taxableIncome > 500000) {
                const slab3 = Math.min(taxableIncome - 500000, 500000);
                slabs.push({
                    range: "₹5,00,001 - ₹10,00,000",
                    incomeInSlab: slab3,
                    rate: "20%",
                    taxInSlab: slab3 * 0.20
                });
            }
            
            if (taxableIncome > 1000000) {
                const slab4 = taxableIncome - 1000000;
                slabs.push({
                    range: "Above ₹10,00,000",
                    incomeInSlab: slab4,
                    rate: "30%",
                    taxInSlab: slab4 * 0.30
                });
            }
        }
        
        return slabs;
    };

    const taxSlabs = taxSlabsFromAPI.length > 0 ? taxSlabsFromAPI : generateTaxSlabs();
    
    // Calculate values for display if not available in API
    const ownMoney = grossSalary;
    const totalDisclosures = totalDeductionsValue + hraExemption;
    const taxableIncome = taxableIncomeFromAPI;

    const monthlyGross = Math.round(grossSalary / 12);
    const monthlyNet = monthlyGross - monthlyTds;

    // =========================================================================
    // 3. GRID CONFIGURATION
    // =========================================================================
    const columnDefs = [
        {
            headerName: "Income Range",
            field: "range",
            flex: 2,
            minWidth: 200,
            cellRenderer: (params) => (
                <span className="color-white font-weight-500">{params.value || '-'}</span>
            )
        },
        {
            headerName: "Tax Rate",
            field: "rate",
            width: 120,
            cellClass: "text-center",
            cellRenderer: (params) => (
                <span className="text-purple font-weight-bold">{params.value || '0%'}</span>
            )
        },
        {
            headerName: "Income in Slab",
            field: "incomeInSlab",
            width: 150,
            cellRenderer: (params) => (
                <span className="color-white">₹{(Number(params.value) || 0).toLocaleString('en-IN')}</span>
            )
        },
        {
            headerName: "Tax Amount",
            field: "taxInSlab",
            width: 150,
            cellRenderer: (params) => {
                const value = Number(params.value) || 0;
                return (
                    <span className={value > 0 ? "text-info font-weight-500" : "color-gray"}>
                        ₹{value.toLocaleString('en-IN')}
                    </span>
                );
            }
        }
    ];

    return (
        <div>
            <Row>
                <Col lg="9" className='mb-4'>
                    {/* Header */}
                    <div className="mb-4 d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className='color-white font-weight-200 mb-1'>Slab-wise TDS Computation</h3>
                            <p className='color-gray font-weight-200 mb-0'>
                                FY {financialYear} • <span className="text-purple">{regimeType === 'new' ? 'New' : 'Old'} Regime</span>
                                <span className="ml-2 badge badge-info small">Based on HR Review Data</span>
                            </p>
                        </div>
                        <button 
                            className="btn btn-outline-purple btn-sm"
                            onClick={() => {
                                setLoading(true);
                                const fetchData = async () => {
                                    try {
                                        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
                                        const userId = userData.user_id || userData.id || "123";
                                        
                                        const payload = {
                                            path: getReviwQueueDeshbordPath,
                                            data: {
                                                userId: userId,
                                                staffId: Number(staffId),
                                                employeeId: "FY2025-26",
                                                section: "all",
                                                financialYear: "2025-26",
                                            },
                                        };
                                        
                                        const response = await dispatch(commonSaveData(payload)).unwrap();
                                        if (response.code === 200 && response.data) {
                                            setApiData(response.data);
                                            toast.success("TDS data refreshed");
                                        }
                                    } catch (err) {
                                        toast.error("Failed to refresh data");
                                        console.error("Refresh error:", err);
                                    } finally {
                                        setLoading(false);
                                    }
                                };
                                fetchData();
                            }}
                        >
                            <RefreshCw className="mr-1" /> Refresh
                        </button>
                    </div>

                    {/* Income Summary Cards - Like PNG */}
                    <div className='setting-border height-auto p-4 mb-4'>
                        <h4 className='text-purple font-weight-200 mb-4 d-flex align-items-center gap-2'>
                            <ScaleBalanced /> Income Summary
                        </h4>
                        <Row className='text-center'>
                            <Col md="3" className="mb-3 mb-md-0">
                                <div className="p-3" style={{ background: 'rgba(155, 81, 224, 0.1)', borderRadius: '8px' }}>
                                    <h3 className='color-white font-weight-300 mb-1'>₹{ownMoney.toLocaleString('en-IN')}</h3>
                                    <span className='color-gray font-13'>Gross Salary</span>
                                </div>
                            </Col>
                            <Col md="3" className="mb-3 mb-md-0">
                                <div className="p-3" style={{ background: 'rgba(0, 255, 136, 0.1)', borderRadius: '8px' }}>
                                    <h3 className='green-text font-weight-300 mb-1'>₹{standardDeduction.toLocaleString('en-IN')}</h3>
                                    <span className='color-gray font-13'>Standard Deduction</span>
                                </div>
                            </Col>
                            <Col md="3" className="mb-3 mb-md-0">
                                <div className="p-3" style={{ background: 'rgba(81, 155, 224, 0.1)', borderRadius: '8px' }}>
                                    <h3 className='text-info font-weight-300 mb-1'>₹{totalDisclosures.toLocaleString('en-IN')}</h3>
                                    <span className='color-gray font-13'>Total Deductions</span>
                                </div>
                            </Col>
                            <Col md="3">
                                <div className="p-3" style={{ background: 'rgba(155, 81, 224, 0.2)', borderRadius: '8px' }}>
                                    <h3 className='text-purple font-weight-300 mb-1'>₹{taxableIncome.toLocaleString('en-IN')}</h3>
                                    <span className='color-gray font-13'>Taxable Income</span>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Tax Slab Grid */}
                    <div className='setting-border height-auto mb-4'>
                        <div className='p-4 border-bottom border-light-opacity'>
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className='text-purple font-weight-200 mb-0 d-flex align-items-center gap-2'>
                                    <Calculator /> Tax Slab Breakdown
                                </h4>
                                <span className={`badge px-3 py-2 ${regimeType === 'new' ? 'badge-purple' : 'badge-gray'}`}>
                                    {regimeType === 'new' ? 'New' : 'Old'} Regime Applied
                                </span>
                            </div>
                        </div>

                        {taxSlabs.length > 0 ? (
                            <div className="ag-theme-quartz custom-grid" style={{ height: "auto", minHeight: "250px", width: "100%" }}>
                                <AgGridReact
                                    rowData={taxSlabs}
                                    columnDefs={columnDefs}
                                    domLayout='autoHeight'
                                    onGridReady={onGridReady}
                                    rowHeight={50}
                                    suppressRowHoverHighlight={false}
                                    defaultColDef={{
                                        resizable: true,
                                        sortable: true
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <p className="color-gray">No tax slabs to display (taxable income is zero)</p>
                            </div>
                        )}
                    </div>

                    {/* Final Tax Calculation - Two Column Layout */}
                    <Row className="mb-4">
                        <Col md="6" className='mb-4 mb-md-0'>
                            <div className='setting-border height-auto p-4 h-100'>
                                <h4 className='text-purple font-weight-200 mb-4'>Final Tax Calculation</h4>
                                
                                <div className='d-flex justify-content-between mb-3'>
                                    <span className='color-gray'>Tax Before Rebate</span>
                                    <span className='color-white'>₹{taxBeforeRebate.toLocaleString('en-IN')}</span>
                                </div>
                                
                                <div className='d-flex justify-content-between mb-3'>
                                    <span className='color-gray'>Rebate u/s 87A</span>
                                    <span className='green-text'>- ₹{rebate87A.toLocaleString('en-IN')}</span>
                                </div>
                                
                                <div className='d-flex justify-content-between mb-4'>
                                    <span className='color-gray'>Tax After Rebate</span>
                                    <span className='color-white font-weight-500'>₹{taxAfterRebate.toLocaleString('en-IN')}</span>
                                </div>
                                
                                <div className='d-flex justify-content-between mb-3'>
                                    <span className='color-gray'>Health & Education Cess (4%)</span>
                                    <span className='text-info'>+ ₹{healthEducationCess.toLocaleString('en-IN')}</span>
                                </div>
                                
                                <hr className="border-light-opacity" />
                                
                                <div className='d-flex justify-content-between align-items-center'>
                                    <span className='color-white font-weight-500'>Annual Tax Liability</span>
                                    <h3 className='mb-0 font-weight-300 text-purple'>₹{annualTaxLiability.toLocaleString('en-IN')}</h3>
                                </div>
                            </div>
                        </Col>

                        <Col md="6">
                            <div className='setting-border height-auto p-4 h-100'>
                                <h4 className='text-purple font-weight-200 mb-4'>System vs HR Override</h4>
                                
                                <div className='d-flex justify-content-between mb-3'>
                                    <span className='color-gray'>System Calculated TDS</span>
                                    <span className='color-white'>₹{systemTds.toLocaleString('en-IN')}</span>
                                </div>
                                
                                <div className='d-flex justify-content-between mb-4'>
                                    <span className='color-gray'>HR Override TDS</span>
                                    <span className='text-warning'>₹{hrOverrideTds.toLocaleString('en-IN')}</span>
                                </div>
                                
                                <hr className="border-light-opacity" />
                                
                                <div className='d-flex justify-content-between align-items-center mb-4'>
                                    <span className='color-white font-weight-500'>Monthly TDS</span>
                                    <h3 className='mb-0 font-weight-300 text-purple'>₹{monthlyTds.toLocaleString('en-IN')}</h3>
                                </div>
                                
                                <div className='d-flex justify-content-between mb-3'>
                                    <span className='color-gray'>Monthly Gross Salary</span>
                                    <span className='color-white'>₹{monthlyGross.toLocaleString('en-IN')}</span>
                                </div>
                                
                                <div className='d-flex justify-content-between'>
                                    <span className='color-gray'>Monthly Net Salary</span>
                                    <span className='green-text font-weight-500'>₹{monthlyNet.toLocaleString('en-IN')}</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>

                {/* Sidebar Info - Right Column */}
                <Col lg="3">
                    <div className='setting-border height-auto p-4 sticky-top' style={{ top: '20px' }}>
                        <h4 className='text-purple font-weight-200 mb-4 d-flex align-items-center gap-2'>
                            <UserTag /> Employee Details
                        </h4>
                        
                        <div className='mb-3'>
                            <label className='color-gray font-12 d-block'>Name</label>
                            <span className='color-white font-weight-500'>{employeeName}</span>
                        </div>
                        
                        <div className='mb-3'>
                            <label className='color-gray font-12 d-block'>Employee ID</label>
                            <span className='color-white'>{employeeId}</span>
                        </div>
                        
                        <div className='mb-3'>
                            <label className='color-gray font-12 d-block'>PAN Number</label>
                            <span className='color-white'>{panNumber}</span>
                        </div>
                        
                        <div className='mb-3'>
                            <label className='color-gray font-12 d-block'>Financial Year</label>
                            <span className='color-white'>FY {financialYear}</span>
                        </div>
                        
                        <div className='mb-3'>
                            <label className='color-gray font-12 d-block'>Tax Regime</label>
                            <span className={`badge ${regimeType === 'new' ? 'badge-purple' : 'badge-warning'}`}>
                                {regimeType === 'new' ? 'New Regime' : 'Old Regime'}
                            </span>
                        </div>
                        
                        <hr className="border-light-opacity" />
                        
                        <div className='mb-3'>
                            <label className='color-gray font-12 d-block'>Effective Tax Rate</label>
                            <div className='d-flex align-items-center'>
                                <h4 className='text-info font-weight-300 mb-0 mr-2'>
                                    {taxableIncome > 0 ? ((annualTaxLiability / taxableIncome) * 100).toFixed(2) : '0'}%
                                </h4>
                                <span className='color-gray small'>of taxable income</span>
                            </div>
                        </div>
                        
                        <hr className="border-light-opacity" />
                        
                        <div className='small color-gray'>
                            <p className='mb-2'>
                                <strong>Note:</strong> Calculations based on {regimeType === 'new' ? 'new' : 'old'} tax regime.
                            </p>
                            <p className='mb-0'>
                                Data sourced from HR review dashboard API.
                            </p>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default SlabWiseTds;